/**
 * System-landscape diagram - pure layout resolution.
 *
 * Resolves a set of selections + a topology config into placed nodes (with box
 * dimensions), column headers, and edges - WITHOUT any React/xyflow dependency.
 * The positioning math mirrors `createSystemLandscapeFlowElements` in `@protoss/ui`
 * (kept in lockstep via the shared layout constants); this version is consumed by the
 * server-side SVG renderer in `@protoss/doc`. Guarded by `system-landscape.test.ts`.
 */
import { ERP_NO_EMBED_HEIGHT, SYSTEM_LANDSCAPE_COLUMN_HEADER_WIDTH, SYSTEM_LANDSCAPE_COLUMN_X, SYSTEM_LANDSCAPE_LAYOUT, SYSTEM_LANDSCAPE_ROW_Y, } from './layout.js';
import { hasSystemLandscapeSelectionContent, } from './types.js';
function nodeDims(variant, kind, hasEmbedded) {
    if (variant === 'compact') {
        return {
            width: SYSTEM_LANDSCAPE_LAYOUT.compactNodeWidth,
            height: SYSTEM_LANDSCAPE_LAYOUT.compactNodeHeight,
        };
    }
    if (kind === 'erp') {
        return {
            width: SYSTEM_LANDSCAPE_LAYOUT.erpNodeWidth,
            height: hasEmbedded ? SYSTEM_LANDSCAPE_LAYOUT.erpNodeHeight : ERP_NO_EMBED_HEIGHT,
        };
    }
    return {
        width: SYSTEM_LANDSCAPE_LAYOUT.regularNodeWidth,
        height: SYSTEM_LANDSCAPE_LAYOUT.regularNodeHeight,
    };
}
/**
 * Resolve selections + config into a placed, framework-free layout.
 * Mirrors the positioning of `createSystemLandscapeFlowElements`:
 * - left/right columns are vertically centered on the ERP's midpoint,
 * - the Taxes node embeds inside ERP (or falls back to a compact standalone node).
 */
export function resolveSystemLandscapeLayout(params) {
    const { selections, config } = params;
    const active = selections.filter(hasSystemLandscapeSelectionContent);
    const selectionById = new Map(active.map((s) => [s.id, s]));
    const embeddedByParentId = new Map();
    for (const selection of active) {
        const placement = config.placements[selection.id];
        if (placement?.mode === 'embedded' &&
            placement.attachToId &&
            selectionById.has(placement.attachToId)) {
            embeddedByParentId.set(placement.attachToId, selection);
        }
    }
    const ROW_ORDER = { top: 0, middle: 1, bottom: 2 };
    const NODE_GAP = 24;
    const erpNodeId = Object.entries(config.placements).find(([, p]) => p.column === 'center' && p.row === 'middle' && p.mode !== 'embedded')?.[0];
    const erpHasEmbedded = erpNodeId != null && embeddedByParentId.has(erpNodeId);
    const erpActualHeight = erpHasEmbedded
        ? SYSTEM_LANDSCAPE_LAYOUT.erpNodeHeight
        : SYSTEM_LANDSCAPE_LAYOUT.regularNodeHeight;
    const erpCenterY = SYSTEM_LANDSCAPE_ROW_Y.middle + erpActualHeight / 2;
    const dynamicYByNodeId = new Map();
    for (const col of ['left', 'right']) {
        const colEntries = active
            .filter((sel) => {
            const p = config.placements[sel.id];
            return p?.column === col && p.mode !== 'embedded';
        })
            .map((sel) => {
            const placement = config.placements[sel.id];
            return placement ? { id: sel.id, row: placement.row } : null;
        })
            .filter((entry) => entry !== null)
            .sort((a, b) => ROW_ORDER[a.row] - ROW_ORDER[b.row]);
        if (colEntries.length === 0)
            continue;
        const totalSpan = colEntries.length * SYSTEM_LANDSCAPE_LAYOUT.regularNodeHeight +
            (colEntries.length - 1) * NODE_GAP;
        const startY = erpCenterY - totalSpan / 2;
        colEntries.forEach((entry, i) => {
            dynamicYByNodeId.set(entry.id, startY + i * (SYSTEM_LANDSCAPE_LAYOUT.regularNodeHeight + NODE_GAP));
        });
    }
    function position(id) {
        const selection = selectionById.get(id);
        const placement = config.placements[id];
        if (!selection || !placement)
            return null;
        if (placement.mode === 'embedded' && placement.attachToId) {
            if (selectionById.has(placement.attachToId))
                return null;
            if (placement.fallback) {
                return {
                    x: SYSTEM_LANDSCAPE_COLUMN_X[placement.fallback.column],
                    y: SYSTEM_LANDSCAPE_ROW_Y[placement.fallback.row],
                    variant: 'compact',
                };
            }
            return null;
        }
        return {
            x: SYSTEM_LANDSCAPE_COLUMN_X[placement.column] + (placement.offsetX ?? 0),
            y: dynamicYByNodeId.get(id) ?? SYSTEM_LANDSCAPE_ROW_Y[placement.row],
            variant: 'default',
        };
    }
    const nodes = [];
    for (const selection of active) {
        const placement = config.placements[selection.id];
        if (placement?.mode === 'embedded' &&
            placement.attachToId &&
            selectionById.has(placement.attachToId)) {
            continue;
        }
        const placed = position(selection.id);
        if (!placed)
            continue;
        const kind = selection.id === 'erp' ? 'erp' : 'regular';
        const embedded = embeddedByParentId.get(selection.id) ?? null;
        const dims = nodeDims(placed.variant, kind, embedded != null);
        nodes.push({
            id: selection.id,
            x: placed.x,
            y: placed.y,
            width: dims.width,
            height: dims.height,
            kind,
            variant: placed.variant,
            label: selection.label,
            currentTool: selection.currentTool ?? null,
            postPlan: selection.postPlan ?? null,
            tone: selection.tone ?? 'neutral',
            embeddedChild: embedded
                ? {
                    label: embedded.label,
                    currentTool: embedded.currentTool ?? null,
                    postPlan: embedded.postPlan ?? null,
                    tone: embedded.tone ?? 'neutral',
                }
                : null,
        });
    }
    const columns = (config.columns ?? []).map((col) => ({
        id: col.id,
        label: col.label,
        x: SYSTEM_LANDSCAPE_COLUMN_X[col.id],
        width: SYSTEM_LANDSCAPE_COLUMN_HEADER_WIDTH[col.id],
    }));
    const nodeIds = new Set(nodes.map((n) => n.id));
    const edges = [];
    for (const rel of config.relationships) {
        if (!nodeIds.has(rel.sourceId) || !nodeIds.has(rel.targetId))
            continue;
        edges.push({
            id: rel.id,
            source: rel.sourceId,
            target: rel.targetId,
            sourceHandle: rel.sourceHandle ?? 'right',
            targetHandle: rel.targetHandle ?? 'left',
            bidirectional: rel.bidirectional ?? false,
        });
    }
    return {
        canvasWidth: SYSTEM_LANDSCAPE_LAYOUT.canvasWidth,
        canvasHeight: SYSTEM_LANDSCAPE_LAYOUT.canvasHeight,
        headerTop: SYSTEM_LANDSCAPE_LAYOUT.headerTop,
        headerHeight: SYSTEM_LANDSCAPE_LAYOUT.headerHeight,
        columns,
        nodes,
        edges,
    };
}
//# sourceMappingURL=elements.js.map